import {
  ALL_INVOICE_VARIABLE_PATHS,
  INVOICE_VARIABLE_GROUPS,
  INVOICE_VARIABLE_NAMESPACE,
} from '@metycle/shared';

import { resolveNoteKind } from '../lib/invoice-label';
import { INVOICE_FIELD_MODEL } from './invoice-field-registry';
import { sideAllows } from './invoice-field-types';

import type { InvoiceFieldDef, InvoiceKindKey, InvoiceSide } from './invoice-field-types';

// Accessors over the invoice field registry — see invoice-field-types.ts for
// the model semantics and invoice-field-registry.ts for the data.

const BY_KEY: ReadonlyMap<string, InvoiceFieldDef> = new Map(
  INVOICE_FIELD_MODEL.map((def) => [def.key, def]),
);

export function invoiceFieldByKey(key: string): InvoiceFieldDef | undefined {
  return BY_KEY.get(key);
}

export function isInvoiceFieldPresent(def: InvoiceFieldDef, kind: InvoiceKindKey): boolean {
  return def.kinds[kind] !== undefined;
}

export function isInvoiceFieldEditable(
  def: InvoiceFieldDef,
  kind: InvoiceKindKey,
  side: InvoiceSide,
): boolean {
  const kindRule = def.kinds[kind];
  return kindRule !== undefined && sideAllows(kindRule.editable, side);
}

export function isInvoiceFieldRequired(
  def: InvoiceFieldDef,
  kind: InvoiceKindKey,
  side: InvoiceSide,
): boolean {
  const kindRule = def.kinds[kind];
  return kindRule !== undefined && sideAllows(kindRule.required ?? false, side);
}

// The fields present for a kind, in document order (group order per the
// shared namespace, then registry order within the group).
export function invoiceFieldsFor(kind: InvoiceKindKey): readonly InvoiceFieldDef[] {
  const groupOrder = new Map<string, number>(
    INVOICE_VARIABLE_GROUPS.map((group, index) => [group, index]),
  );
  return INVOICE_FIELD_MODEL.filter((def) => isInvoiceFieldPresent(def, kind)).sort(
    (a, b) => (groupOrder.get(a.group) ?? 0) - (groupOrder.get(b.group) ?? 0),
  );
}

// The namespace binding a field feeds on the document, or null when it never
// renders (workflow/assembly input).
export function invoiceFieldDocPath(def: InvoiceFieldDef): string | null {
  return def.docPath === undefined ? def.key : def.docPath;
}

// A document binding is either a scalar namespace path or a repeating
// section name (adjustment_lines binds as a {{#section}}, not a scalar).
const DOCUMENT_BINDINGS: ReadonlySet<string> = new Set([
  ...ALL_INVOICE_VARIABLE_PATHS,
  ...Object.values(INVOICE_VARIABLE_NAMESPACE).flatMap((group) =>
    group.repeating ? [group.section] : [],
  ),
]);

export function invoiceFieldRendersOnDocument(def: InvoiceFieldDef): boolean {
  const path = invoiceFieldDocPath(def);
  return path !== null && DOCUMENT_BINDINGS.has(path);
}

// The operator-supplied override fields with no upstream system value — the
// document shows an em-dash for each until the operator fills it. This is the
// set the review dialog surfaces as the BL-body inputs.
export function operatorSuppliedOverrideFields(): readonly InvoiceFieldDef[] {
  return INVOICE_FIELD_MODEL.filter((def) => def.storage === 'override' && !def.systemSourced);
}

// Canonical kind for an invoice row, tolerating the legacy spellings
// ('Final Settlement', 'Adjustment' + note_type). Null when unknown.
export function resolveInvoiceKind(invoice: {
  invoice_type: string | null;
  note_type: string | null;
}): InvoiceKindKey | null {
  const noteKind = resolveNoteKind(invoice);
  if (noteKind !== null) return noteKind === 'Credit' ? 'Credit_Note' : 'Debit_Note';
  if (invoice.invoice_type === 'Advance') return 'Advance';
  if (invoice.invoice_type === 'Provisional') return 'Provisional';
  if (invoice.invoice_type === 'Final_Settlement' || invoice.invoice_type === 'Final Settlement') {
    return 'Final_Settlement';
  }
  return null;
}
